import {

  APP_TIMEZONE,

  easternTodayYmd,

  easternWallClockDate,

  formatTime12hFrom24,

  formatTimeAmPmFromStored,

  parseStoredDatetimeParts,

} from './easternTime';



/** Parse "H:MM AM/PM" (flexible spaces). Returns 24h hours and minutes, or null. */

export function parseTimeAmPm(input: string): { h: number; m: number } | null {

  const s = input.trim().replace(/\s+/g, ' ');

  const m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i) ?? s.match(/^(\d{1,2}):(\d{2})(AM|PM)$/i);

  if (!m) return null;

  let h = parseInt(m[1], 10);

  const min = parseInt(m[2], 10);

  const ap = m[3].toUpperCase();

  if (min > 59 || h < 1 || h > 12) return null;

  if (ap === 'PM' && h !== 12) h += 12;

  if (ap === 'AM' && h === 12) h = 0;

  return { h, m: min };

}



function parseMeridiemToken(token?: string): 'AM' | 'PM' | null {

  if (!token) return null;

  const t = token.replace(/\./g, '').toLowerCase();

  if (t === 'a' || t === 'am') return 'AM';

  if (t === 'p' || t === 'pm') return 'PM';

  return null;

}



/** Guess AM/PM when the user omits it (e.g. shorthand "930"). */

export function inferMeridiemForHour(hour12: number): 'AM' | 'PM' {

  if (hour12 === 12) return 'PM';

  if (hour12 >= 8 && hour12 <= 11) return 'AM';

  if (hour12 >= 1 && hour12 <= 7) return 'PM';

  return 'AM';

}



function to24h(hour12: number, minute: number, meridiem: 'AM' | 'PM'): { h: number; m: number } | null {

  if (hour12 < 1 || hour12 > 12 || minute < 0 || minute > 59) return null;

  let h = hour12;

  if (meridiem === 'PM' && h !== 12) h += 12;

  if (meridiem === 'AM' && h === 12) h = 0;

  return { h, m: minute };

}



/**

 * Parse flexible time text: "9:30 AM", "930", "930p", "9", "2130" (24h), etc.

 * Returns 24h hours and minutes, or null.

 */

export function parseFlexibleTimeInput(input: string): { h: number; m: number } | null {

  const s = input.trim().replace(/\s+/g, ' ');

  if (!s) return null;



  const standard = parseTimeAmPm(s);

  if (standard) return standard;



  const colon = s.match(/^(\d{1,2}):(\d{2})(?:\s*(a\.?m\.?|p\.?m\.?))?$/i);

  if (colon) {

    const hour = parseInt(colon[1], 10);

    const minute = parseInt(colon[2], 10);

    const ap = parseMeridiemToken(colon[3]) ?? inferMeridiemForHour(hour);

    return to24h(hour, minute, ap);

  }



  const compact = s.match(/^(\d{1,4})(?:\s*(a\.?m\.?|p\.?m\.?))?$/i);

  if (!compact) return null;



  const ap = parseMeridiemToken(compact[2]);

  const digits = compact[1];

  let hour: number;

  let minute: number;



  if (digits.length <= 2) {

    hour = parseInt(digits, 10);

    minute = 0;

  } else if (digits.length === 3) {

    hour = parseInt(digits.slice(0, 1), 10);

    minute = parseInt(digits.slice(1), 10);

  } else {

    const hPart = parseInt(digits.slice(0, 2), 10);

    minute = parseInt(digits.slice(2), 10);

    if (minute > 59) return null;

    if (!ap && hPart >= 13 && hPart <= 23) {

      return { h: hPart, m: minute };

    }

    if (hPart < 1 || hPart > 12) return null;

    hour = hPart;

  }



  if (hour < 1 || hour > 12 || minute < 0 || minute > 59) return null;

  const meridiem = ap ?? inferMeridiemForHour(hour);

  return to24h(hour, minute, meridiem);

}



export function normalizeFlexibleTimeInput(

  input: string

): { ok: true; canonical: string } | { ok: false; error: string } {

  const trimmed = input.trim();

  if (!trimmed) return { ok: true, canonical: '' };

  const parsed = parseFlexibleTimeInput(trimmed);

  if (!parsed) {

    return { ok: false, error: 'Enter a time like 9:30 AM, 930, or 1:30p' };

  }

  return { ok: true, canonical: formatTime12hFrom24(parsed.h, parsed.m) };

}



export function previewFlexibleTimeInput(input: string): string | null {

  const trimmed = input.trim();

  if (!trimmed) return null;

  const result = normalizeFlexibleTimeInput(trimmed);

  return result.ok && result.canonical ? result.canonical : null;

}



/** `visitDate` = YYYY-MM-DD (US Eastern wall clock). */

export function toMysqlDateTime(visitDate: string, t: { h: number; m: number }): string {

  const parts = visitDate.split('-').map(Number);

  if (parts.length !== 3 || parts.some(n => Number.isNaN(n))) return '';

  const pad = (n: number) => String(n).padStart(2, '0');

  return `${visitDate} ${pad(t.h)}:${pad(t.m)}:00`;

}



/** For grouping DB visits into segments (same Eastern wall-clock minute). */

export function scheduleKey(scheduled: string | undefined | null): string {

  if (!scheduled) return '';

  return scheduled.length >= 16 ? scheduled.slice(0, 16) : scheduled;

}



/** 12h Eastern wall-clock time from a Date whose local getters match Eastern. */

export function formatTimeAmPmFromDate(d: Date): string {

  if (Number.isNaN(d.getTime())) return '';

  return formatTime12hFrom24(d.getHours(), d.getMinutes());

}



export function formatTimeAmPmFromScheduled(iso: string | undefined | null): string {

  return formatTimeAmPmFromStored(iso);

}



function anchorYmd(visitDate: string): { y: number; mo: number; d: number } {

  const s =

    visitDate && /^\d{4}-\d{2}-\d{2}$/.test(visitDate.trim())

      ? visitDate.trim()

      : easternTodayYmd();

  const p = s.split('-').map(Number);

  if (p.length !== 3 || p.some(n => Number.isNaN(n))) {

    const t = easternWallClockDate();

    return { y: t.getFullYear(), mo: t.getMonth() + 1, d: t.getDate() };

  }

  return { y: p[0], mo: p[1], d: p[2] };

}



export function formatVisitDateTimeLabel(date: string, time: string): string {

  const d = timeAmPmStringToDate(date, time);

  if (!d) return time || 'Select time';

  return d.toLocaleString('en-US', {

    timeZone: APP_TIMEZONE,

    month: 'short',

    day: 'numeric',

    year: 'numeric',

    hour: 'numeric',

    minute: '2-digit',

  });

}



/**

 * Value for 12h time pickers. Empty/invalid time string → `null` (so the field is clear).

 * Day uses `visitDate` (YYYY-MM-DD) or today (Eastern) so only the clock portion is meaningful.

 */

export function timeAmPmStringToDate(visitDate: string, timeStr: string): Date | null {

  const t = parseFlexibleTimeInput((timeStr || '').trim());

  if (!t) return null;

  const { y, mo, d } = anchorYmd(visitDate);

  return new Date(y, mo - 1, d, t.h, t.m, 0, 0);

}



export { easternTodayYmd, easternWallClockDate, easternDatetimeLocalValue, dateToYmdInEastern } from './easternTime';


